/**
 * Boucle `requestAnimationFrame` : dt capé à 50 ms, en pause quand l'onglet
 * est masqué (`document.hidden`) — ARCHITECTURE.md §4.3.
 */

export interface FrameLoop {
  readonly running: boolean;
  start(): void;
  stop(): void;
}

const MAX_DT = 0.05;

/**
 * Plus long pas de **temps de jeu** confié au cœur en un seul appel
 * (`game.tick`) : le cap réel de {@link createFrameLoop}. Avec le facteur de
 * vitesse du debug (A99), la vue découpe son `dt × facteur` en pas d'au plus
 * ce délai — le cœur ne traite qu'un lancer du *Larbin* par `tick`.
 */
export const GAME_STEP_MAX = MAX_DT;

/**
 * Découpe un temps de jeu `dtGame` (s) en `count` pas égaux de `step` ≤
 * {@link GAME_STEP_MAX}. Un `dt` valide de ×1 (≤ 50 ms) reste un seul pas : le
 * déroulé à ×1 ne change pas. Un `dt` nul, négatif ou invalide ne donne aucun pas.
 */
export function splitGameDt(dtGame: number): { count: number; step: number } {
  if (!Number.isFinite(dtGame) || dtGame <= 0) return { count: 0, step: 0 };
  const count = Math.max(1, Math.ceil(dtGame / GAME_STEP_MAX - 1e-9));
  return { count, step: dtGame / count };
}

export function createFrameLoop(onFrame: (dtSeconds: number) => void): FrameLoop {
  let rafId: number | null = null;
  let running = false;
  let last = 0;

  const frame = (ts: number): void => {
    if (!running) return;
    if (typeof document !== 'undefined' && document.hidden) {
      // Onglet masqué : on ne progresse pas, mais on reste planifié pour
      // reprendre dès que l'onglet redevient visible.
      last = 0;
      rafId = requestAnimationFrame(frame);
      return;
    }
    if (last === 0) {
      last = ts;
      rafId = requestAnimationFrame(frame);
      return;
    }
    const dt = Math.min(MAX_DT, Math.max(0, (ts - last) / 1000));
    last = ts;
    onFrame(dt);
    rafId = requestAnimationFrame(frame);
  };

  return {
    get running() {
      return running;
    },
    start(): void {
      if (running) return;
      running = true;
      last = 0;
      rafId = requestAnimationFrame(frame);
    },
    stop(): void {
      running = false;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
    },
  };
}
